import { c as _c } from "react/compiler-runtime";
import { isEinFormat } from '@onefootprint/core';
import { IcoEye16 } from '@onefootprint/icons';
import { useState } from 'react';
import { type FieldValues, type Path, type PathValue, type RegisterOptions, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import IconButton from '../../icon-button';
import LoadingSpinner from '../../loading-spinner';
import FormInput, { type FormInputProps } from './form-input';
const SCRUBBED_EIN_DISPLAY = '••-•••••••';
export type FormEinProps<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>;
  /**
   * Whether the EIN value is scrubbed (not decryptable).
   * When true, the component displays a masked placeholder and the form value remains `undefined`.
   */
  isScrubbed?: boolean;
  /**
   * Callback to reveal a scrubbed EIN value. When provided and isScrubbed is true,
   * a reveal button will be shown. The callback should perform step-up authentication
   * and return the revealed EIN value.
   */
  onReveal?: () => Promise<string | undefined>;
  /**
   * Whether the reveal action is currently in progress.
   */
  isRevealing?: boolean;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
} & Omit<FormInputProps<TFieldValues>, 'name' | 'rules' | 'type' | 'autoComplete'>;
export default function FormEin(t0) {
  const $ = _c(54);
  let onReveal;
  let props;
  let rules;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      rules,
      size: t1,
      isScrubbed: t2,
      onReveal,
      isRevealing: t3,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = onReveal;
    $[2] = props;
    $[3] = rules;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    onReveal = $[1];
    props = $[2];
    rules = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
  }
  const size = t1 === undefined ? "default" : t1;
  const isScrubbed = t2 === undefined ? false : t2;
  const isRevealing = t3 === undefined ? false : t3;
  const {
    t
  } = useTranslation("ui");
  const {
    setValue
  } = useFormContext();
  const [showScrubbedValue, setShowScrubbedValue] = useState(isScrubbed);
  const [isRevealed, setIsRevealed] = useState(false);
  let t4;
  if ($[7] !== isScrubbed || $[8] !== t) {
    t4 = value => {
      if (!value) {
        return true;
      }
      if (isScrubbed && value === SCRUBBED_EIN_DISPLAY) {
        return true;
      }
      if (!isEinFormat(value)) {
        return t(_temp);
      }
      return true;
    };
    $[7] = isScrubbed;
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== isRevealed || $[11] !== isScrubbed) {
    t5 = value_0 => {
      if (isScrubbed && !isRevealed && value_0 === SCRUBBED_EIN_DISPLAY) {
        return;
      }
      return value_0;
    };
    $[10] = isRevealed;
    $[11] = isScrubbed;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== rules || $[14] !== t4 || $[15] !== t5) {
    t6 = {
      validate: t4,
      setValueAs: t5,
      ...rules
    };
    $[13] = rules;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const einRules = t6;
  let t7;
  if ($[17] !== isScrubbed || $[18] !== onReveal || $[19] !== props || $[20] !== setValue || $[21] !== showScrubbedValue) {
    t7 = () => {
      const canEdit = !isScrubbed || !onReveal;
      if (showScrubbedValue && canEdit) {
        setShowScrubbedValue(false);
        setValue(props.name, "" as PathValue<TFieldValues, Path<TFieldValues>>);
      }
    };
    $[17] = isScrubbed;
    $[18] = onReveal;
    $[19] = props;
    $[20] = setValue;
    $[21] = showScrubbedValue;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const handleFocus = t7;
  let t8;
  if ($[23] !== isRevealed || $[24] !== isScrubbed || $[25] !== props || $[26] !== setValue || $[27] !== showScrubbedValue) {
    t8 = e => {
      if (!e.target.value && !showScrubbedValue && isScrubbed && !isRevealed) {
        setShowScrubbedValue(true);
        setValue(props.name, SCRUBBED_EIN_DISPLAY as PathValue<TFieldValues, Path<TFieldValues>>);
      }
    };
    $[23] = isRevealed;
    $[24] = isScrubbed;
    $[25] = props;
    $[26] = setValue;
    $[27] = showScrubbedValue;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  const handleBlur = t8;
  let t9;
  if ($[29] !== isRevealing || $[30] !== onReveal || $[31] !== props || $[32] !== setValue) {
    t9 = async () => {
      if (!onReveal || isRevealing) {
        return;
      }
      const revealedValue = await onReveal();
      if (revealedValue) {
        setIsRevealed(true);
        setShowScrubbedValue(false);
        setValue(props.name, revealedValue as PathValue<TFieldValues, Path<TFieldValues>>);
      }
    };
    $[29] = isRevealing;
    $[30] = onReveal;
    $[31] = props;
    $[32] = setValue;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  const handleReveal = t9;
  const showRevealButton = isScrubbed && !isRevealed && onReveal;
  let t10;
  if ($[34] !== isRevealed || $[35] !== showScrubbedValue) {
    t10 = showScrubbedValue && !isRevealed ? {
      value: SCRUBBED_EIN_DISPLAY
    } : {
      mask
    };
    $[34] = isRevealed;
    $[35] = showScrubbedValue;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  const scrubbedOverrideProps = t10;
  let t11;
  if ($[37] !== handleReveal || $[38] !== isRevealing || $[39] !== showRevealButton || $[40] !== size || $[41] !== t) {
    t11 = () => {
      if (!showRevealButton) {
        return;
      }
      if (isRevealing) {
        return <LoadingSpinner size="compact" className="mr-3" />;
      }
      return <IconButton icon={IcoEye16} color="quaternary" size={size === "compact" ? "tiny" : "default"} aria-label={t(_temp2)} onClick={handleReveal} />;
    };
    $[37] = handleReveal;
    $[38] = isRevealing;
    $[39] = showRevealButton;
    $[40] = size;
    $[41] = t;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  const renderSuffixComponent = t11;
  const t12 = showScrubbedValue && isScrubbed && !isRevealed && !!onReveal;
  let t13;
  if ($[43] !== renderSuffixComponent) {
    t13 = renderSuffixComponent();
    $[43] = renderSuffixComponent;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== einRules || $[46] !== handleBlur || $[47] !== handleFocus || $[48] !== props || $[49] !== scrubbedOverrideProps || $[50] !== size || $[51] !== t12 || $[52] !== t13) {
    t14 = <FormInput {...props} size={size} rules={einRules} type="tel" autoComplete="off" {...scrubbedOverrideProps} placeholder="XX-XXXXXXX" onFocusCapture={handleFocus} onBlur={handleBlur} readOnly={t12} suffixComponent={t13} />;
    $[45] = einRules;
    $[46] = handleBlur;
    $[47] = handleFocus;
    $[48] = props;
    $[49] = scrubbedOverrideProps;
    $[50] = size;
    $[51] = t12;
    $[52] = t13;
    $[53] = t14;
  } else {
    t14 = $[53];
  }
  return t14;
}
function _temp2($_0) {
  return $_0.components.form.ein["aria-label"].reveal;
}
function _temp($) {
  return $.components.form.ein.errors.invalid;
}
const mask = {
  numericOnly: true,
  delimiters: ['-'],
  blocks: [2, 7]
};